import { statusTintFamily, type TintFamily } from '@/components/molecules/status-tokens';
import type { SchedulerBooking } from '@/types/scheduler';

/**
 * Marker/timeline-block colors shared by the dispatch timeline and the map
 * companion so a stop reads the same in both views. Kept as raw hex/HSL
 * (not CSS tokens) because Google Maps markers can't consume CSS variables.
 */
const STATUS_VAR: Record<string, string> = {
  UNSCHEDULED: '#f59e0b', // amber-500
  RESERVED: '#6366f1', // indigo-500
  SCHEDULED: '#6366f1', // indigo-500
  CONFIRMED: '#10b981', // emerald-500
  DISPATCHED: '#6366f1', // indigo-500
  IN_PROGRESS: '#3b82f6', // blue-500
  ON_HOLD: '#f59e0b', // amber-500
  COMPLETED: '#10b981', // emerald-500
  SKIPPED: '#71717a', // zinc-500
  CANCELLED: '#ef4444', // red-500
  CANCELLATION_PENDING: '#f59e0b', // amber-500
  EXPIRED: '#71717a', // zinc-500
  EN_ROUTE: '#06b6d4', // cyan-500
  ARRIVED: '#0ea5e9', // sky-500
};

/** §5.1 color-by options (NOT-329 adds `confirmation`; default is `jobtype`). */
export type ColorBy = 'jobtype' | 'status' | 'priority' | 'confirmation' | 'tech';

/** Every selectable mode — the one list the route search whitelists validate against. */
export const COLOR_BY_VALUES: ColorBy[] = ['jobtype', 'status', 'priority', 'confirmation', 'tech'];

/**
 * NOT-329 — the mode to fall back to when the URL doesn't pin one. Resolved at
 * the point of use rather than in `validateSearch`, so an absent param stays
 * absent from the URL and the persisted preference can still be restored.
 */
export const DEFAULT_COLOR_BY: ColorBy = 'jobtype';

export type BookingTint =
  | { family: TintFamily; customHex?: never }
  | { family?: never; customHex: string };

// Customer-confirmation hexes for the map (the signal is a fast-follow backend
// field; until it lands the timeline mode degrades to status — see §9).
const CONFIRMATION_VAR: Record<string, string> = {
  confirmed: '#10b981', // emerald-500
  unconfirmed: '#f59e0b', // amber-500
  no_response: '#71717a', // zinc-500
};

// Priority palette — semantic, mirrors the status hues (no invented accent).
const PRIORITY_VAR: Record<string, string> = {
  EMERGENCY: '#ef4444', // red-500
  HIGH: '#f59e0b', // amber-500
  NORMAL: '#6366f1', // indigo-500
  LOW: '#71717a', // zinc-500
};

export function statusColor(status: string): string {
  return STATUS_VAR[status.toUpperCase()] ?? '#6366f1';
}

// NOT-329 §6 — once a job is live (dispatched+), the tile's meta line shows the
// state word + a subtle pulse (the numeric ETA is fast-follow). Null = not live.
const LIVE_STATUS_LABEL: Record<string, string> = {
  DISPATCHED: 'Dispatched',
  EN_ROUTE: 'En route',
  ARRIVED: 'On site',
  IN_PROGRESS: 'In progress',
};

export function liveStatusLabel(status: string): string | null {
  return LIVE_STATUS_LABEL[status.toUpperCase()] ?? null;
}

// NOT-329 — a time-grid tile is pinned to a duration-derived height (FullCalendar
// absolutely positions it to the harness's inset), so a half-hour visit only gets
// ~24px — less than the dense 3-line body needs. At or under this length the tile
// drops to a single identity line instead of three clipped ones.
export const SHORT_TILE_MINUTES = 30;

/** True when a timed tile is too short to carry the dense multi-line body. */
export function isShortTile(start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false;
  return (end.getTime() - start.getTime()) / 60_000 <= SHORT_TILE_MINUTES;
}

export function priorityColor(priority?: string | null): string {
  return PRIORITY_VAR[(priority ?? 'NORMAL').toUpperCase()] ?? '#6366f1';
}

/** Stable hue per id (FNV-ish hash → HSL) — shared by tech + job-type coloring. */
function hashHue(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return `hsl(${hash % 360} 65% 45%)`;
}

export function techColor(techId: string): string {
  return hashHue(techId);
}

// NOT-329 — representative hex per token family (≈the -500 shade), so the map
// pins can match the board's `jobtype` Trade tint (same family → same meaning).
const FAMILY_HEX: Record<TintFamily, string> = {
  emerald: '#10b981',
  teal: '#14b8a6',
  blue: '#3b82f6',
  indigo: '#6366f1',
  yellow: '#eab308',
  amber: '#f59e0b',
  gray: '#6b7280',
  purple: '#a855f7',
  pink: '#ec4899',
  zinc: '#71717a',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  orange: '#f97316',
  destructive: '#ef4444',
};

export function familyToHex(family: TintFamily): string {
  return FAMILY_HEX[family];
}

export function bookingJobTypeColor(booking: SchedulerBooking): string | null {
  const raw = booking.jobType?.color?.trim().toLowerCase();
  if (!raw || !/^#[0-9a-f]{6}$/.test(raw)) return null;
  return raw;
}

export function jobTypeColorMix(hex: string, amount: number, fallback: string): string {
  return `color-mix(in oklab, ${hex} ${amount}%, ${fallback})`;
}

/**
 * How much of the job-type colour survives into the tile fill. The selectable
 * palette is already pastel, so the old 14% mixed a pastel into the page
 * background and read as "no colour at all". 40% is the ceiling: the tile still
 * clears 4.5:1 against `--foreground` in both themes for every hex the custom
 * picker can produce (worst case ≈ 5:1 — pure black on the light theme; the
 * curated palette stays ≥ 6.8:1). Anything above ~42% drops a black job type
 * below AA on light.
 */
const JOB_TYPE_FILL_MIX = 40;
/** The border keeps its ~1.5x lead over the fill so the tile still has an edge. */
const JOB_TYPE_BORDER_MIX = 60;

/**
 * The single `jobtype` resolution chain: the persisted hex, else the Trade
 * palette hex for the booking's service type, else null (the caller falls back
 * to status colouring). Every `jobtype` surface — map pins, board tiles, calendar
 * tiles — goes through this, so one booking can never read as two colours on one
 * screen. Deliberately stops at null rather than hashing the service-type id: a
 * synthesized hue has no counterpart in the tile palette (see `bookingTint`).
 */
export function resolveJobTypeHex(
  booking: SchedulerBooking,
  resolveJobTypeFamily?: (serviceTypeId: string) => TintFamily | undefined,
): string | null {
  const persisted = bookingJobTypeColor(booking);
  if (persisted) return persisted;
  const family = booking.serviceType?.id
    ? resolveJobTypeFamily?.(booking.serviceType.id)
    : undefined;
  return family ? familyToHex(family) : null;
}

/**
 * Shared pastel treatment for job-type-coloured calendar surfaces. `undefined`
 * is load-bearing — it's how a tile falls through to its status styling, and it
 * lines up with `bookingColor` falling through to `statusColor`.
 */
export function bookingJobTypeStyle(
  booking: SchedulerBooking,
  resolveJobTypeFamily?: (serviceTypeId: string) => TintFamily | undefined,
) {
  const color = resolveJobTypeHex(booking, resolveJobTypeFamily);
  if (!color) return undefined;

  return {
    backgroundColor: jobTypeColorMix(color, JOB_TYPE_FILL_MIX, 'var(--background)'),
    borderColor: jobTypeColorMix(color, JOB_TYPE_BORDER_MIX, 'transparent'),
  };
}

/**
 * The color a booking gets on the map pins (Google Maps can't read tokens).
 * `resolveJobTypeFamily` (the same Trade resolver the board uses) keeps `jobtype`
 * pins on the curated Trade palette so the docked map and the timeline agree.
 */
export function bookingColor(
  booking: SchedulerBooking,
  colorBy: ColorBy,
  resolveJobTypeFamily?: (serviceTypeId: string) => TintFamily | undefined,
): string {
  switch (colorBy) {
    case 'tech':
      return booking.technicianId ? techColor(booking.technicianId) : statusColor(booking.status);
    case 'jobtype':
      // Same chain (and the same status fallback) as the tiles — a pin never
      // synthesizes a hue the tile palette can't express.
      return resolveJobTypeHex(booking, resolveJobTypeFamily) ?? statusColor(booking.status);
    case 'priority':
      return priorityColor(booking.priority);
    case 'confirmation':
      return (
        CONFIRMATION_VAR[booking.customerConfirmationStatus ?? ''] ?? statusColor(booking.status)
      );
    case 'status':
    default:
      return statusColor(booking.status);
  }
}

// NOT-329 — priority → token family (mirrors the map hexes, no invented accent).
const PRIORITY_FAMILY: Record<string, TintFamily> = {
  EMERGENCY: 'destructive',
  HIGH: 'amber',
  NORMAL: 'indigo',
  LOW: 'zinc',
};

// Curated families for tech coloring — a stable hash into the palette (never an
// arbitrary hsl) keeps every tile on-token and dark-safe.
const TECH_FAMILIES: TintFamily[] = [
  'blue',
  'teal',
  'amber',
  'indigo',
  'emerald',
  'orange',
  'purple',
  'pink',
  'cyan',
  'sky',
];

function hashFamily(value: string): TintFamily {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return TECH_FAMILIES[hash % TECH_FAMILIES.length];
}

/**
 * The tile fill source for the active color-by mode. `customHex` carries every
 * valid persisted job-type color, including the configured default.
 */
export function bookingTint(
  booking: SchedulerBooking,
  colorBy: ColorBy,
  resolveJobTypeFamily?: (serviceTypeId: string) => TintFamily | undefined,
): BookingTint {
  switch (colorBy) {
    case 'priority':
      return { family: PRIORITY_FAMILY[(booking.priority ?? 'NORMAL').toUpperCase()] ?? 'indigo' };
    case 'jobtype': {
      const jobTypeColor = bookingJobTypeColor(booking);
      if (jobTypeColor) return { customHex: jobTypeColor };
      const fam = booking.serviceType?.id ? resolveJobTypeFamily?.(booking.serviceType.id) : null;
      return { family: fam ?? statusTintFamily(booking.status) };
    }
    case 'confirmation':
      switch (booking.customerConfirmationStatus) {
        case 'confirmed':
          return { family: 'emerald' };
        case 'unconfirmed':
          return { family: 'amber' };
        case 'no_response':
          return { family: 'zinc' };
        default:
          return { family: statusTintFamily(booking.status) };
      }
    case 'tech':
      return {
        family: booking.technicianId
          ? hashFamily(booking.technicianId)
          : statusTintFamily(booking.status),
      };
    case 'status':
    default:
      return { family: statusTintFamily(booking.status) };
  }
}
